import { Color, Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled, { css } from 'styled-components';

import { workflowStepIcon } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree';
import { FakeWorkflowStepType, WorkflowStepType } from 'dashboard/models/workflow-version';
import { Label, Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';

const Card = styled.div<{
  disabled: boolean;
}>`
  display: flex;
  width: 100%;
  border: 2px solid ${Color.CoreSlate50};
  border-radius: ${Spacing[2]};
  padding: 14px;
  background-color: ${Color.PrimaryWhite};
  cursor: pointer;
  align-items: center;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color.background.disabled};
      cursor: not-allowed;
      user-select: none;

      p,
      label {
        color: ${({ theme }) => theme.color.text.disabled};
      }
    `}
`;

const Icon = styled.div<{ disabled: boolean }>`
  width: 36px;
  height: 36px;
  margin-right: ${Spacing[3]};
  padding: 6px;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  background: ${Color.CorePurple100};

  & svg {
    color: ${({ disabled }) => (disabled ? Color.BorderIconDisabled : Color.CorePurple600)};
  }
`;

const Text = styled('div')`
  display: flex;
  flex-direction: column;
`;

const Description = styled(Paragraph)`
  font-size: 12px;
  font-weight: ${FontWeight.Regular};
  line-height: 22px;
  letter-spacing: unset;
`;

type StepTypeConfig = {
  icon: JSX.Element;
  title: string;
  description: string;
};

const CONFIGS: Record<WorkflowStepType | FakeWorkflowStepType, StepTypeConfig> = {
  [WorkflowStepType.Action]: {
    icon: workflowStepIcon(WorkflowStepType.Action),
    title: 'Action',
    description: 'Perform a specific action in Persona',
  },
  [WorkflowStepType.Parallel]: {
    icon: workflowStepIcon(WorkflowStepType.Parallel),
    title: 'Parallel',
    description: 'Execute multiple branches at the same time',
  },
  [WorkflowStepType.Wait]: {
    icon: workflowStepIcon(WorkflowStepType.Wait),
    title: 'Wait',
    description: 'Wait for a specified event/action',
  },
  [WorkflowStepType.Conditional]: {
    icon: workflowStepIcon(WorkflowStepType.Conditional),
    title: 'Conditional',
    description: 'Set up logic to determine route execution',
  },
  [WorkflowStepType.Loop]: {
    icon: workflowStepIcon(WorkflowStepType.Loop),
    title: 'Loop',
    description: 'Loop through a list of items',
  },
  [WorkflowStepType.Module]: {
    icon: workflowStepIcon(WorkflowStepType.Module),
    title: 'Module',
    description: 'Execute reusable logic',
  },
  [FakeWorkflowStepType.MapOutput]: {
    icon: workflowStepIcon(FakeWorkflowStepType.MapOutput),
    title: 'Array Map Output',
    description: 'List of results from each loop',
  },
  [WorkflowStepType.Output]: {
    icon: workflowStepIcon(WorkflowStepType.Output),
    title: 'Output',
    description: 'Specify the output parameters',
  },
  [WorkflowStepType.Placeholder]: {
    icon: <></>,
    title: '',
    description: '',
  },
  [WorkflowStepType.Input]: {
    icon: <></>,
    title: '',
    description: '',
  },
};

interface StepConfigTypeCardProps {
  type?: WorkflowStepType | FakeWorkflowStepType;
  icon?: JSX.Element;
  title?: string;
  description?: string;
  disabled?: boolean;
  onClickStepType?: () => void;
}

const StepConfigTypeCard: VFC<StepConfigTypeCardProps> = ({
  type,
  icon: iconOverride,
  title: titleOverride,
  description: descriptionOverride,
  disabled = false,
  onClickStepType,
}) => {
  const { icon, title, description } = type
    ? CONFIGS[type]
    : {
        icon: iconOverride,
        title: titleOverride,
        description: descriptionOverride,
      };

  return (
    <Card
      aria-label={`Select type: ${title}`}
      disabled={disabled}
      {...(onClickStepType
        ? {
            id: `workflow-step-config__${type}`,
            role: 'button',
            // Cannot use `pointer-events` to disable click as it interferes with `cursor` css
            onClick: () => !disabled && onClickStepType(),
          }
        : {})}
    >
      <Icon disabled={disabled}>{icon}</Icon>
      <Text>
        <Label size='md' weight='bold' role='heading'>
          {title}
        </Label>
        {!!description && <Description size='sm'>{description}</Description>}
      </Text>
    </Card>
  );
};

export default StepConfigTypeCard;
